import SunAccent from "@/components/feature/SunAccent";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import type { ExperienceDetail } from "@/mocks/experienceDetails";

interface ExperienceHeroProps {
  detail: ExperienceDetail;
  breadcrumbs: BreadcrumbItem[];
}

export default function ExperienceHero({ detail, breadcrumbs }: ExperienceHeroProps) {
  const facts = [
    { icon: "ri-time-line", label: "Duration", value: detail.duration },
    { icon: "ri-group-line", label: "Group size", value: detail.groupSize },
    { icon: "ri-sun-line", label: "Best season", value: detail.bestSeason },
    { icon: "ri-map-pin-line", label: "Location", value: detail.location },
  ];

  return (
    <section className="relative min-h-[75vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={detail.heroImage}
          width={1920}
          height={1080}
          alt={`${detail.title} — ${detail.tagline} at ${detail.location}, Konaseema`}
          title={`${detail.title} - Rythugaru agri tourism experience`}
          className="w-full h-full object-cover object-top"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/80"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 pt-28 md:pt-32 pb-10 md:pb-14">
        <Breadcrumbs
          items={breadcrumbs}
        />

        <div className="flex items-center gap-3 mb-4">
          <SunAccent circleClassName="w-10 h-10" iconClassName="text-xl" />
          <span className="text-3xl">{detail.emoji}</span>
          <span className="bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
            {detail.badge}
          </span>
        </div>

        <h1 className="font-heading text-4xl md:text-6xl leading-tight text-background-50 font-semibold max-w-3xl">
          {detail.title}
        </h1>
        <p className="mt-4 text-lg md:text-2xl text-accent-300 font-medium">{detail.tagline}</p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a
            href="#reserve"
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-check-line text-lg"></i>
            Reserve Your Date
          </a>
          <a
            href="#enquire"
            className="inline-flex items-center gap-2 bg-background-50/90 text-foreground-900 border border-background-200 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Send an Enquiry
          </a>
        </div>
      </div>

      <div className="relative w-full px-4 md:px-8 pb-10 md:pb-14">
        <div className="bg-background-50/95 backdrop-blur-md rounded-lg border border-background-200 grid grid-cols-2 lg:grid-cols-4 divide-y-2 lg:divide-y-0 lg:divide-x divide-background-200">
          {facts.map((fact) => (
            <div key={fact.label} className="p-4 md:p-6 flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                <i className={`${fact.icon} text-xl`}></i>
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                  {fact.label}
                </p>
                <p className="text-sm font-medium text-foreground-900 mt-0.5">{fact.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}